import { AnalyticsService, SearchComponentEvents } from 'base/analytics';
import { getProjectUrl } from 'base/blocks_routes';
import { isSafari, writeToClipboard } from 'base/clipboard';
import {
  applyCopySettings,
  localStorageCopySettings,
} from 'base/copy_settings';
import { getProjectId, relativeTime, UnreachableError } from 'base/helpers';
import { LibraryService, PublicComponent } from 'base/library_service';
import { ServiceHttpError } from 'base/service_error';
import { Button } from 'base/ui/button';
import { formatError } from 'base/ui/form';
import { IconView } from 'base/ui/icons';
import { InfiniteScroll, Loader, RenderItems } from 'base/ui/infinite_scroll';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import { Scrollable } from 'base/ui/scrollable';
import { ToastQueue } from 'base/ui/toast';
import {
  TextRegularLight,
  TextSmallLight,
  TextTinyLight,
} from 'base/ui/typography';
import { useDebounce } from 'base/useDebounce';
import { useStableCallback } from 'base/useStableCallback';
import {
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from 'blocks/auth/routes';
import { BlocksService, ListProjectNamesResponse } from 'blocks/blocks_service';
import { fuzzyTextSearch } from 'blocks/editor/add_panel/fuzzy_text_search';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import componentImage from './search-component.file.svg';
import projectImage from './search-project.file.svg';
import uiElementImage from './search-ui-element.file.svg';
import styles from './search.css';

type TrackSearch = (searchEvent: Omit<SearchComponentEvents, 'query'>) => void;

export const SearchResults = observer(function _SearchResults({
  query,
  mode,
  toastQueue,
  libraryService,
  blocksService,
  analytics,
}: {
  query: string;
  mode: SearchMode;
  toastQueue: ToastQueue;
  libraryService: LibraryService;
  blocksService: Pick<BlocksService, 'listProjectNames'>;
  analytics: AnalyticsService;
}) {
  const trackDebounced = useDebounce(
    // This needs to be bound otherwise the wrong `this` is used … I love JS "classes"
    analytics.track.bind(analytics),
    1000,
  );

  useEffect(() => {
    trackDebounced({
      event: 'search:query_updated',
      query,
      mode,
    });
  }, [mode, query, trackDebounced]);

  const track = useStableCallback<TrackSearch>((event) =>
    analytics.track({ ...event, query, mode }),
  );

  if (!query && mode === 'webflow') {
    return (
      <SearchScrollable>
        <EmptyState
          title="Looking for something?"
          description={
            "Type a keyword and we'll search the Webflow Library for a component or UI element"
          }
        />
      </SearchScrollable>
    );
  }
  if (!query && mode === 'react') {
    return (
      <SearchScrollable>
        <EmptyState
          title="Looking for something?"
          description={
            "Type a keyword and we'll search the React Library for a component."
          }
        />
      </SearchScrollable>
    );
  }

  if (mode === 'projects') {
    return (
      <SearchScrollable>
        <ProjectsSearchResults
          query={query}
          blocksService={blocksService}
          analytics={analytics}
        />
      </SearchScrollable>
    );
  } else {
    return (
      <ComponentSearchResults
        mode={mode}
        query={query}
        toastQueue={toastQueue}
        libraryService={libraryService}
        track={track}
      />
    );
  }
});

function ProjectsSearchResults({
  query,
  blocksService,
  analytics,
}: {
  query: string;
  blocksService: Pick<BlocksService, 'listProjectNames'>;
  analytics: AnalyticsService;
}) {
  const [error, setError] = React.useState<unknown>();
  const [res, setRes] = React.useState<ListProjectNamesResponse | undefined>();

  const track = useStableCallback((projectToken: string) => {
    analytics.track({
      event: 'search:project_link_clicked',
      project_id: getProjectId(projectToken),
      query,
      mode: 'projects',
    });
  });

  React.useEffect(() => {
    blocksService.listProjectNames().then(setRes).catch(setError);
  }, [blocksService]);

  if (error) {
    return <ErrorState error={error} />;
  }
  if (!res) {
    return <LoadingResults />;
  }
  const matching = fuzzyTextSearch(
    res.projects,
    query,
    (p) => p.name || 'Untitled Project',
  );
  if (matching.length === 0) {
    return query ? (
      <EmptyState
        title="No matching projects"
        description="Try a different search"
      />
    ) : (
      <EmptyState
        title="No projects have been created"
        description="Once you have created a project, you can find it here."
      />
    );
  }
  return (
    <Rows spacing={0}>
      {matching.map((p) => (
        <SearchResult
          key={p.token}
          href={getProjectUrl(p.token)}
          title={p.name || 'Untitled Project'}
          description={`${
            p.ownershipType === 'team' ? 'Team' : 'Personal'
          } • Edited ${relativeTime(p.updatedAt).toLowerCase()}`}
          typeImage={projectImage}
          onClick={() => track(p.token)}
        />
      ))}
    </Rows>
  );
}

function ComponentSearchResults({
  mode,
  query,
  toastQueue,
  libraryService,
  track,
}: {
  mode: 'webflow' | 'react';
  query: string;
  toastQueue: ToastQueue;
  libraryService: LibraryService;
  track: TrackSearch;
}) {
  const [previewStore] = React.useState(() => new PreviewStore());
  const searchLoader = React.useCallback<Loader<PublicComponent>>(
    async (continuation) => {
      const res = await libraryService.findPublicComponents(
        {
          search: query,
          // For react components we filter out `ui-elements` as they are distributed
          // as an NPM package so it doesn't make sense to have a page for them.
          filterKind: mode === 'react' ? 'component' : undefined,
        },
        continuation,
      );
      return {
        items: res.components,
        continuation: res.continuation || undefined,
      };
    },
    [query, libraryService, mode],
  );
  const searchRender = React.useCallback<RenderItems<PublicComponent>>(
    (items) => {
      if (items.length === 0) {
        return (
          <EmptyState title="No results" description="Try a different search" />
        );
      }
      return items.map((c) => (
        <SearchResult
          href={urlForComponent(c, mode)}
          key={c.componentSlug}
          title={c.name}
          description={KIND_NAMES[c.kind]}
          typeImage={KIND_IMAGES[c.kind]}
          {...previewStore.makeCallbacks(c)}
          onClick={() =>
            track({
              event: 'search:component_link_clicked',
              mode,
              component_slug: c.componentSlug,
              category_slug: c.categorySlug,
            })
          }
          actions={
            mode === 'webflow' && (
              <>
                <Button
                  icon={IconView}
                  href={`/preview?cid=${c.categorySlug}/${c.componentSlug}`}
                  tooltip="Open Preview"
                  onClick={() =>
                    track({
                      event: 'search:component_preview_button_clicked',
                      component_slug: c.componentSlug,
                      category_slug: c.categorySlug,
                      mode,
                    })
                  }
                />
                <CopyComponentButton
                  component={c}
                  toastQueue={toastQueue}
                  libraryService={libraryService}
                  onClick={() =>
                    track({
                      event: 'search:component_copy_button_clicked',
                      component_slug: c.componentSlug,
                      category_slug: c.categorySlug,
                      mode,
                    })
                  }
                />
              </>
            )
          }
        />
      ));
    },
    [libraryService, previewStore, toastQueue, track, mode],
  );
  return (
    <PreviewSplit store={previewStore}>
      <InfiniteScroll
        loader={searchLoader}
        render={searchRender}
        LoadingState={LoadingResults}
        ErrorState={ErrorState}
      />
    </PreviewSplit>
  );
}

class PreviewStore {
  @observable.ref
  current: PublicComponent | undefined = undefined;

  constructor() {
    makeObservable(this);
  }

  makeCallbacks = (c: PublicComponent) => {
    return {
      onMouseEnter: () => {
        this.current = c;
      },
      onMouseLeave: () => {
        if (this.current?.componentSlug === c.componentSlug) {
          this.current = undefined;
        }
      },
    };
  };
}

const PreviewSplit = observer(function _PreviewSplit({
  store,
  children,
}: {
  store: PreviewStore;
  children: React.ReactNode;
}) {
  const c = store.current;
  return (
    <div className={styles.previewSplit}>
      <SearchScrollable>{children}</SearchScrollable>
      <div className={styles.previewPanel}>
        {c?.thumbnailUrl ? (
          <div
            className={styles.previewImage}
            key={c.thumbnailUrl}
            style={{
              backgroundImage: `url(${c.thumbnailUrl})`,
            }}
          />
        ) : (
          <SpacerGrow />
        )}
        <TextSmallLight color="grey" align="center">
          {c?.name || ''}
        </TextSmallLight>
      </div>
    </div>
  );
});

function SearchScrollable({ children }: { children: React.ReactNode }) {
  return (
    <div className={styles.scrollable}>
      <Scrollable>
        <div className={styles.scrollableInner}>{children}</div>
      </Scrollable>
    </div>
  );
}

function CopyComponentButton({
  component,
  toastQueue,
  libraryService,
  onClick,
}: {
  component: PublicComponent;
  toastQueue: ToastQueue;
  libraryService: LibraryService;
  onClick: () => void;
}) {
  const [loading, setLoading] = React.useState(false);

  const handleClick = React.useCallback(async () => {
    if (isSafari()) {
      toastQueue.push({
        label:
          "Safari isn't supported yet. Please use Chrome, Firefox, or Edge",
      });
      return;
    }

    if (loading) return;
    setLoading(true);
    onClick();
    try {
      const settings = localStorageCopySettings.value;
      const comp = applyCopySettings(
        settings,
        await libraryService.copyPublicComponent({
          componentSlug: component.componentSlug,
          trigger: 'search',
          analyticsStyle: settings.style,
          analyticsSpacing: settings.spacing,
        }),
      );
      writeToClipboard({
        'application/json': JSON.stringify(comp),
      });
      toastQueue.push({ label: 'Copied to clipboard' });
    } catch (error) {
      if (
        error instanceof ServiceHttpError &&
        error.status === 403 &&
        error.message.includes('Free user can not')
      ) {
        window.location.href = authRouteToUrl({
          path: 'upgrade',
          context: {
            redirect: getCurrentLocationForRedirect(),
          },
        });
      } else {
        toastQueue.push({ label: formatError(error) });
      }
    } finally {
      setLoading(false);
    }
  }, [loading, onClick, toastQueue, libraryService, component.componentSlug]);

  return <Button label="Copy" loading={loading} onClick={handleClick} />;
}

export function urlForComponent(
  c: PublicComponent,
  mode: 'webflow' | 'react' = 'webflow',
) {
  if (mode === 'webflow') {
    switch (c.kind) {
      case 'component':
        return `/components/${c.webflowSlug}`;
      // We only have item pages for "component" components right now
      case 'ui-element':
        return `/ui-elements?ui-elements-categories=${c.categorySlug}`;
      case 'page-template':
        return `/page-templates?page-tags=${c.categorySlug}`;
      default:
        throw new UnreachableError(c.kind);
    }
  } else {
    return `/react-components/${c.webflowSlug}`;
  }
}

const KIND_NAMES: Record<PublicComponent['kind'], string> = {
  component: '• Component',
  'ui-element': '• UI Element',
  'page-template': '• Page Template',
};

const KIND_IMAGES: Record<PublicComponent['kind'], string> = {
  component: componentImage,
  'ui-element': uiElementImage,
  'page-template': projectImage,
};

function LoadingResults() {
  return (
    <Rows spacing={8}>
      {[0, 1, 2].map((i) => (
        <div
          className={styles.loadingResult}
          style={{ animationDelay: `${i * 0.1}s` }}
          key={i}
        />
      ))}
    </Rows>
  );
}

function ErrorState({ error }: { error: unknown }) {
  return (
    <EmptyState
      title={formatError(error)}
      description="Please refresh the page or try a different search"
    />
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className={styles.empty}>
      <Rows spacing={8} xalign="center">
        <TextRegularLight align="center">{title}</TextRegularLight>
        <TextSmallLight color="grey" align="center">
          {description}
        </TextSmallLight>
      </Rows>
    </div>
  );
}

function SearchResult({
  href,
  title,
  description,
  typeImage,
  actions,
  onClick,
  ...passthrough
}: {
  href: string;
  title: string;
  description: string;
  typeImage: string;
  actions?: React.ReactNode;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  React.useEffect(() => {
    // treat unmounting as a mouse leave
    return passthrough.onMouseLeave;
  }, [passthrough.onMouseLeave]);
  return (
    <div className={styles.result} {...passthrough}>
      <Button variant="tertiary" href={href} onClick={onClick}>
        <div className={styles.resultInner}>
          <img src={typeImage} className={styles.resultTypeImage} />
          <div className={styles.resultTitle}>
            <TextSmallLight lineClamp={1}>{title}</TextSmallLight>
          </div>
          <TextTinyLight color="grey" lineClamp={1}>
            {description}
          </TextTinyLight>
        </div>
      </Button>
      <div className={styles.resultActions}>
        <Inline spacing={8}>{actions}</Inline>
      </div>
    </div>
  );
}

export type SearchMode = 'projects' | 'webflow' | 'react';
export const SEARCH_MODE_OPTIONS: { label: string; value: SearchMode }[] = [
  { label: 'Projects', value: 'projects' },
  { label: 'Webflow Library', value: 'webflow' },
  { label: 'React Components', value: 'react' },
];
